import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import styles from './Auth.module.css';
import { CheckIcon } from '../components/Icons';

const Register = ({ onSwitchToLogin, inviteCode = '' }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    invite: inviteCode || ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [appliedInviteCode, setAppliedInviteCode] = useState(inviteCode);
  const { register } = useAuth();

  useEffect(() => {
    setAppliedInviteCode(inviteCode);
    if (inviteCode) {
      setFormData((prev) => ({
        ...prev,
        invite: inviteCode,
      }));
    }
  }, [inviteCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'invite' ? value.toUpperCase() : value
    }));

    // 清除对应字段的错误
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    if (name === 'invite') {
      setAppliedInviteCode(value.toUpperCase());
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // 用户名验证
    if (!formData.username.trim()) {
      newErrors.username = '请输入用户名';
    } else if (formData.username.length < 3) {
      newErrors.username = '用户名至少3个字符';
    } else if (formData.username.length > 20) {
      newErrors.username = '用户名不能超过20个字符';
    } else if (!/^[a-zA-Z0-9_\u4e00-\u9fa5]+$/.test(formData.username)) {
      newErrors.username = '用户名只能包含字母、数字、下划线和中文';
    }

    // 密码验证
    if (!formData.password) {
      newErrors.password = '请输入密码';
    } else if (formData.password.length < 6) {
      newErrors.password = '密码至少6个字符';
    } else if (formData.password.length > 50) {
      newErrors.password = '密码不能超过50个字符';
    }

    // 确认密码验证
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = '请确认密码';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = '两次输入的密码不一致';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSuccess(false);
    
    try {
      const result = await register(formData.username, formData.password, formData.invite || undefined);
      
      if (result.success) {
        setSuccess(true);
        setFormData({
          username: '',
          password: '',
          confirmPassword: '',
          invite: inviteCode || ''
        });
        
        // 3秒后自动跳转到登录页
        setTimeout(() => {
          onSwitchToLogin();
        }, 3000);
      } else {
        setErrors({ general: result.message || '注册失败' });
      }
    } catch (error) {
      setErrors({ general: '注册失败，请重试' });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={styles.authContainer}>
        <div className={`${styles.authCard} card`}>
          <div className={styles.successMessage}>
            <div className={styles.successIcon}><CheckIcon size={48} /></div>
            <h2>注册成功！</h2>
            <p>您的账户已创建成功，3秒后自动跳转到登录页面</p>
            <button
              className="btn btn-primary"
              onClick={onSwitchToLogin}
            >
              立即登录
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.authContainer}>
      <div className={`${styles.authCard} card`}>
        <div className={styles.authHeader}>
          <h1 className={styles.authTitle}>创建账户</h1>
          <p className={styles.authSubtitle}>加入我们，开启阅读之旅</p>
        </div>

        <form className={styles.authForm} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="invite" className={styles.formLabel}>
              邀请码（可选）
            </label>
            <div className={styles.inviteInputRow}>
              <input
                type="text"
                id="invite"
                name="invite"
                className={`${styles.formInput} ${styles.inviteInput}`}
                placeholder="请输入邀请码"
                value={formData.invite}
                onChange={handleChange}
                disabled={loading || Boolean(inviteCode)}
              />
              {inviteCode && (
                <span className={styles.inviteBadge}>来自邀请链接</span>
              )}
            </div>
            {appliedInviteCode && !inviteCode && (
              <div className={styles.formHint}>当前已使用邀请码：{appliedInviteCode}</div>
            )}
          </div>

          {errors.general && (
            <div className={`${styles.errorMessage} ${styles.generalError}`}>
              {errors.general}
            </div>
          )}

          <div className={styles.formGroup}>
            <label htmlFor="username" className={styles.formLabel}>
              用户名
            </label>
            <input
              type="text"
              id="username"
              name="username"
              className={`${styles.formInput} ${errors.username ? styles.error : ''}`}
              placeholder="请输入用户名（3-20个字符）"
              value={formData.username}
              onChange={handleChange}
              disabled={loading}
            />
            {errors.username && (
              <div className={styles.errorMessage}>{errors.username}</div>
            )}
            <div className={styles.formHint}>
              支持中文、英文、数字和下划线
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.formLabel}>
              密码
            </label>
            <input
              type="password"
              id="password"
              name="password"
              className={`${styles.formInput} ${errors.password ? styles.error : ''}`}
              placeholder="请输入密码（至少6个字符）"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
            />
            {errors.password && (
              <div className={styles.errorMessage}>{errors.password}</div>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="confirmPassword" className={styles.formLabel}>
              确认密码
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              className={`${styles.formInput} ${errors.confirmPassword ? styles.error : ''}`}
              placeholder="请再次输入密码"
              value={formData.confirmPassword}
              onChange={handleChange}
              disabled={loading}
            />
            {errors.confirmPassword && (
              <div className={styles.errorMessage}>{errors.confirmPassword}</div>
            )}
          </div>

          <button
            type="submit"
            className={`${styles.authSubmitBtn} btn btn-primary`}
            disabled={loading}
          >
            {loading ? '注册中...' : '注册'}
          </button>
        </form>

        <div className={styles.authFooter}>
          <p className={styles.authSwitchText}>
            已有账户？
            <button
              type="button"
              className={styles.authSwitchBtn}
              onClick={onSwitchToLogin}
              disabled={loading}
            >
              立即登录
            </button>
          </p>
        </div>

        <div className={styles.authTerms}>
          <p className={styles.termsText}>
            注册即表示您同意我们的
            <a href="#" className={styles.termsLink}>服务条款</a>
            和
            <a href="#" className={styles.termsLink}>隐私政策</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
